import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useSetTopbarCrumbs } from "@marekl/adminer/shell";
import { Page, Card, Input, Button, Alert, FormStack, InlineLoading, ResponsiveGrid, Text } from "@marekl/adminer/components";
import { ScanInput } from "../../components/ScanInput";
import { CustomerCombobox } from "../../components/CustomerCombobox";
import {
  useVoucherLookup,
  useCreateCounterSale,
  type LookupResponse,
} from "../../lib/queries/vouchers";
import { classifyScan } from "../../lib/scan";
import { ApiError } from "../../lib/api";
import { formatCurrency } from "@marekl/adminer/format";
import { notifySuccess } from "@marekl/adminer/api";
import { useScanKeyboardCapture } from "../../lib/useScanKeyboardCapture";
import styles from "./CounterSalePage.module.css";

interface CustomerForm {
  name: string;
  phone: string;
  email: string;
  note: string;
}

interface CustomerErrors {
  name?: string;
  phone?: string;
  email?: string;
}

const EMPTY_FORM: CustomerForm = { name: "", phone: "", email: "", note: "" };

function lookupErrorMessage(err: unknown): string {
  if (err instanceof ApiError) {
    const body = err.body as { message?: string } | null;
    if (body?.message) return body.message;
  }
  return "Nepodařilo se ověřit kód, zkuste to prosím znovu.";
}

function lookupBadKindMessage(res: LookupResponse): string {
  switch (res.kind) {
    case "voucher":
      return "Tento kód už je přiřazený k existujícímu voucheru — pro úpravu otevřete detail voucheru.";
    case "claim":
      return "Naskenovaný kód patří do rezervovaného rozsahu certifikátů (029…). Pro směnu použijte stránku Směna certifikátu.";
    case "unknown":
    default:
      return "Kód nebyl nalezen v bance dostupných čárových kódů.";
  }
}

export function CounterSalePage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const lookup = useVoucherLookup();
  const sale = useCreateCounterSale();

  const [scanValue, setScanValue] = useState("");
  const [scanError, setScanError] = useState<string | null>(null);
  const [bank, setBank] = useState<LookupResponse["bank"] | null>(null);
  const [form, setForm] = useState<CustomerForm>(EMPTY_FORM);
  const [errors, setErrors] = useState<CustomerErrors>({});
  const [saleError, setSaleError] = useState<string | null>(null);

  const scanRef = useRef<HTMLInputElement>(null);

  useScanKeyboardCapture(scanRef, (v) => {
    setScanValue(v);
    setScanError(null);
  });

  useEffect(() => {
    scanRef.current?.focus();
  }, []);

  // Pre-fill from ?barcode= (e.g. navigated from barcode bank dashboard card)
  useEffect(() => {
    const barcode = searchParams.get("barcode");
    if (!barcode) return;
    setScanValue(barcode);
    handleScan(barcode);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function resetAll() {
    setScanValue("");
    setScanError(null);
    setBank(null);
    setForm(EMPTY_FORM);
    setErrors({});
    setSaleError(null);
    sale.reset();
    lookup.reset();
    setTimeout(() => scanRef.current?.focus(), 0);
  }

  async function handleScan(raw: string) {
    setScanError(null);
    const { kind, normalized } = classifyScan(raw);
    if (normalized === "") {
      setScanError("Naskenujte nebo zadejte čárový kód voucheru.");
      return;
    }
    if (kind === "claim") {
      setScanError(lookupBadKindMessage({ kind: "claim" } as LookupResponse));
      return;
    }
    try {
      const res = await lookup.mutateAsync(normalized);
      if (res.kind !== "bank_available" || !res.bank) {
        setScanError(lookupBadKindMessage(res));
        setBank(null);
        return;
      }
      setBank(res.bank);
    } catch (err) {
      setScanError(lookupErrorMessage(err));
      setBank(null);
    }
  }

  function validateForm(): CustomerErrors {
    const next: CustomerErrors = {};
    if (!form.name.trim()) next.name = "Jméno zákazníka je povinné.";
    if (!form.phone.trim() && !form.email.trim()) {
      next.phone = "Zadejte alespoň telefon nebo e-mail zákazníka.";
    }
    if (form.email.trim() && !form.email.includes("@")) {
      next.email = "E-mail nemá platný tvar.";
    }
    return next;
  }

  async function handleIssue() {
    if (!bank) return;
    const v = validateForm();
    setErrors(v);
    setSaleError(null);
    if (Object.keys(v).length > 0) return;
    try {
      const out = await sale.mutateAsync({
        barcode: bank.barcode,
        customer_name: form.name.trim(),
        customer_phone: form.phone.trim() || null,
        customer_email: form.email.trim() || null,
        note: form.note.trim() || null,
      });
      notifySuccess(`Voucher ${out.barcode} byl vystaven.`);
      navigate(`/vouchers/${out.id}`);
    } catch (err) {
      setSaleError(lookupErrorMessage(err));
    }
  }

  const vt = bank?.voucher_type;

  useSetTopbarCrumbs([{ label: "Skenování", href: "/dashboard" }, { label: "Prodej voucheru na recepci" }]);

  return (
    <>
      <Page.Content>
        <Page.Header
          title="Prodej voucheru"
          subtitle="platbu vyřiďte v pokladně, zde jen evidence"
          subtitleVariant="tagline"
        />

        <Card
          header={
            <Card.Header title="1. Naskenujte fyzický voucher" subtitle="typ a cena se doplní automaticky" />
          }
        >
          <ScanInput
            ref={scanRef}
            value={scanValue}
            onChange={(v) => {
              setScanValue(v);
              if (scanError) setScanError(null);
            }}
            onScan={handleScan}
            placeholder="Skenujte kód z papírového voucheru…"
            disabled={lookup.isPending || !!bank}
            variant="compact"
          />
          <div className={styles.scanActions}>
            <Button
              type="button"
              onClick={resetAll}
              disabled={!scanValue && !bank && !scanError}
            >
              Smazat
            </Button>
            <Button
              type="button"
              variant="primary"
              onClick={() => handleScan(scanValue)}
              disabled={lookup.isPending || !!bank}
              loading={lookup.isPending}
            >
              Ověřit kód
            </Button>
          </div>

          {scanError && (
            <div className={styles.alertSpacer}>
              <Alert variant="err">{scanError}</Alert>
            </div>
          )}

          {bank && vt && (
            <div className={styles.alertSpacer}>
              <Alert variant="ok">
                Kód <Text variant="mono">{bank.barcode}</Text> · typ{" "}
                <strong>{vt.name}</strong> · cena{" "}
                <strong>{formatCurrency(vt.price_czk)}</strong> · platnost{" "}
                <strong>{vt.validity_months} měsíců</strong>. Kód je k
                dispozici.
              </Alert>
            </div>
          )}
        </Card>

        {bank && vt && (
          <ResponsiveGrid className={styles.resultGrid}>
            <Card
              header={
                <Card.Header title="2. Zákazník" />
              }
            >
              <FormStack>
                <CustomerCombobox
                  label="Jméno a příjmení"
                  placeholder="např. Petr Novák"
                  value={form.name}
                  onChange={(name) => setForm({ ...form, name })}
                  onSelectCustomer={(c) =>
                    setForm({
                      ...form,
                      name: c.name,
                      phone: c.phone ?? form.phone,
                      email: c.email ?? form.email,
                    })
                  }
                  error={errors.name}
                />
                <div className={styles.formRow}>
                  <Input
                    label="Telefon"
                    placeholder="+420 …"
                    value={form.phone}
                    onChange={(e) =>
                      setForm({ ...form, phone: e.target.value })
                    }
                    error={errors.phone}
                    hint={!errors.phone ? "alespoň telefon nebo e-mail" : undefined}
                    autoComplete="off"
                  />
                  <Input
                    label="E-mail"
                    placeholder="zakaznik@example.cz"
                    type="email"
                    value={form.email}
                    onChange={(e) =>
                      setForm({ ...form, email: e.target.value })
                    }
                    error={errors.email}
                    autoComplete="off"
                  />
                </div>
                <Input
                  label="Poznámka"
                  placeholder="volitelně"
                  value={form.note}
                  onChange={(e) =>
                    setForm({ ...form, note: e.target.value })
                  }
                  autoComplete="off"
                />
              </FormStack>
            </Card>

            <Card
              header={
                <Card.Header title="3. Přehled" subtitle="před potvrzením" />
              }
            >
              <dl className={styles.summary}>
                <div className={styles.summaryRow}>
                  <dt>Kód voucheru</dt>
                  <dd><Text variant="mono">{bank.barcode}</Text></dd>
                </div>
                <div className={styles.summaryRow}>
                  <dt>Typ</dt>
                  <dd>{vt.name}</dd>
                </div>
                <div className={styles.summaryRow}>
                  <dt>Cena</dt>
                  <dd>{formatCurrency(vt.price_czk)}</dd>
                </div>
                <div className={styles.summaryRow}>
                  <dt>Jízd</dt>
                  <dd>{vt.default_rides}</dd>
                </div>
                <div className={styles.summaryRow}>
                  <dt>Platnost</dt>
                  <dd>{vt.validity_months} měsíců od vystavení</dd>
                </div>
              </dl>
              {saleError && (
                <div className={styles.alertSpacer}>
                  <Alert variant="err">{saleError}</Alert>
                </div>
              )}
              <div className={styles.confirmActions}>
                <Button onClick={resetAll} disabled={sale.isPending}>
                  Zrušit
                </Button>
                <Button
                  variant="primary"
                  onClick={handleIssue}
                  loading={sale.isPending}
                >
                  Vydat voucher zákazníkovi
                </Button>
              </div>
            </Card>
          </ResponsiveGrid>
        )}

        {!bank && lookup.isPending && (
          <InlineLoading mono className={styles.blockLoading}>Ověřujeme kód…</InlineLoading>
        )}

        <div className={styles.backLink}>
          <Link to="/dashboard">Zpět na nástěnku</Link>
        </div>
      </Page.Content>
    </>
  );
}
